// RPTEXPORT1 (docs/build-briefs/RPTEXPORT1-pdf-and-google-sheets-export.md): "Export PDF" and "Export to Google Sheets", the
// entries of the one Export menu (UIFIX1, SF-1) on the canned report result and the saved custom report result, in the location
// and franchisor consoles. reportToolbar.ts draws the menu; this file owns what each entry does.
//
// PDF is a file download from the server. RPTEXPORT1-F2: Export to Google Sheets signs the person in to Google in THIS browser
// at export time (Google Identity Services, scope drive.file only), asks our server for the report rows (same permission and
// audit as CSV), creates the sheet in the person's own Drive with the Sheets API from here and opens it in a new tab. The Google
// access token is held in a local variable for that one export, never stored, never sent to our server. A deployment without a
// Google client id shows the Google Sheets entry disabled, with "Not set up" and its reason, only inside the menu, and everything
// else works.

import { saveBlob } from "./consoleDownload.ts";
import type { ToolbarItem } from "./reportToolbar.ts";
import { preloadGoogleIdentity } from "./googleSheets.ts";
import { createReportSheet, requestExportToken } from "./googleSheetsReport.ts";
import { SHEETS_MESSAGES, parseSheetsPayload } from "./googleSheetsReportModel.ts";
import {
  clientIdFromOptions,
  exportFailureMessage,
  exportOptionsPath,
  safeSheetUrl,
  sheetsButtonFace,
  withFormat,
  type SheetsState,
} from "./reportExportButtonsModel.ts";

export interface ExportButtonsHost {
  prefix: string;
  request<T>(
    method: "GET" | "POST",
    path: string,
    body?: unknown,
  ): Promise<{ status: number; body: T }>;
  file(
    path: string,
    body: unknown,
  ): Promise<{ status: number; blob?: Blob; filename?: string; message?: string }>;
  active(): boolean;
}

export interface ExportTarget {
  path: string;
  body: unknown;
}

export interface ExportButtonsConfig {
  /** Test id prefix: `${prefix}-pdf`, `${prefix}-sheets`. */
  testid: string;
  /** The export request for the result on screen, or undefined when there is nothing to export now. */
  target(): ExportTarget | undefined;
  /** True while the screen is busy or has no result. */
  disabled(): boolean;
  /** One plain line under the toolbar; empty clears it. `link` adds an "Open the sheet" link; `ok` marks a line that reports
   * something that worked (it is not drawn as an error). */
  notify(message: string, link?: string, ok?: boolean): void;
  redraw(): void;
}

export function createExportButtons(
  host: ExportButtonsHost,
  config: ExportButtonsConfig,
): {
  items(): ToolbarItem[];
  load(): void;
  destroy(): void;
} {
  const optionsPath = exportOptionsPath(host.prefix);
  let state: SheetsState = "loading";
  /** The PUBLIC Google OAuth web client id (not a secret); null until the options are read or when none is configured. */
  let clientId: string | null = null;
  let working = false;
  let destroyed = false;

  async function load(): Promise<void> {
    try {
      const reply = await host.request<unknown>("GET", optionsPath);
      if (destroyed || !host.active()) return;
      const id = clientIdFromOptions(reply.status, reply.body);
      if (id === undefined) state = "error";
      else if (id === null) state = "not_set_up";
      else {
        clientId = id;
        state = "ready";
        // Load Google's script now so the sign-in window can open inside the click.
        void preloadGoogleIdentity();
      }
    } catch {
      state = "error";
    }
    config.redraw();
  }

  async function exportPdf(): Promise<void> {
    const target = config.target();
    if (working || target === undefined) return;
    working = true;
    config.notify("");
    try {
      const reply = await host.file(withFormat(target.path, "pdf"), target.body);
      if (destroyed || !host.active()) return;
      if (reply.status === 200 && reply.blob) saveBlob(reply.blob, reply.filename ?? "report.pdf");
      else config.notify(reply.message ?? "Could not download this report.");
    } catch {
      config.notify("Could not download this report.");
    } finally {
      working = false;
    }
    if (!destroyed && host.active()) config.redraw();
  }

  async function exportSheets(): Promise<void> {
    const target = config.target();
    if (working || target === undefined || state === "not_set_up" || state === "loading") return;
    if (state === "error" || clientId === null) {
      config.notify("");
      await load();
      if (state === "error" && !destroyed) config.notify(SHEETS_MESSAGES.optionsFailed);
      return;
    }
    // Both windows open inside the click so the browser allows them: the new tab for the sheet (sent to it when it exists,
    // closed if nothing was made) and Google's sign-in window, started by requestExportToken before its first await.
    const tab = window.open("about:blank", "_blank");
    const signIn = requestExportToken(clientId);
    working = true;
    config.notify("");
    config.redraw();
    try {
      const access = await signIn;
      if (!access.ok) {
        tab?.close();
        config.notify(access.message);
        return;
      }
      // The token stays in this function: it is only ever the Authorization header of the Sheets calls below.
      // The report rows, after the server's own view permission, report.export permission and audit.
      const reply = await host.request<unknown>("POST", withFormat(target.path, "sheets"), target.body);
      if (destroyed || !host.active()) {
        tab?.close();
        return;
      }
      if (reply.status !== 200) {
        tab?.close();
        const code = (reply.body as { error?: unknown } | null)?.error;
        if (code === "google_sheets_not_configured") state = "not_set_up";
        config.notify(exportFailureMessage(reply.status, reply.body));
        return;
      }
      const payload = parseSheetsPayload(reply.body);
      if (payload === null) {
        tab?.close();
        config.notify(SHEETS_MESSAGES.unreadable);
        return;
      }
      config.notify(SHEETS_MESSAGES.creating, undefined, true);
      const made = await createReportSheet(access.accessToken, payload);
      if (destroyed || !host.active()) return;
      const url = made.ok ? safeSheetUrl(made.url) : undefined;
      if (url !== undefined) {
        if (tab !== null) tab.location.href = url;
        config.notify(tab === null ? SHEETS_MESSAGES.ready : SHEETS_MESSAGES.opened, url, true);
      } else {
        tab?.close();
        config.notify(made.ok ? SHEETS_MESSAGES.createFailed : made.message);
      }
    } catch {
      tab?.close();
      config.notify(SHEETS_MESSAGES.createFailed);
    } finally {
      working = false;
    }
    if (!destroyed && host.active()) config.redraw();
  }

  /** UIFIX1 (SF-1): the PDF and Google Sheets entries of the one Export menu. A state that cannot be used carries its reason. */
  function items(): ToolbarItem[] {
    const blocked = config.disabled() || working;
    const none = config.target() === undefined;
    const face = sheetsButtonFace(state);
    return [
      { testid: `${config.testid}-pdf`, label: "Export PDF", disabled: blocked || none, reason: "", run: () => void exportPdf() },
      {
        testid: `${config.testid}-sheets`,
        label: state === "not_set_up" ? "Google Sheets" : face.label,
        disabled: face.disabled || blocked || none,
        reason: state === "not_set_up" ? "Not set up. Ask a Snip & Sip team member to finish the setup." : "",
        run: () => void exportSheets(),
      },
    ];
  }

  return {
    items,
    load: () => void load(),
    destroy: () => {
      destroyed = true;
    },
  };
}
